import { useMemo, useRef, useState } from 'react';
import {
  ROLE_LABEL,
  encodeQr,
  passkeyDelete,
  passkeyList,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  trustedDeviceRevoke,
  trustedDevices,
  twofaBackupRegenerate,
  twofaDisable,
  twofaSessionEnroll,
  twofaSessionVerify,
  twofaStatus,
  type ApiError,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { base64urlToBuffer, bufferToBase64url } from '../../auth/webauthn';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { ErrLine, I, chipT, notServed, pre, sx, useApi, useGuarded, usePage } from './kit';

/**
 * The otpauth URI as a QR code, drawn by our own encoder (packages/shared qr.ts), inside
 * the design's placeholder box. Four light modules of quiet zone around it.
 */
function OtpQr({ uri }: { uri: string }) {
  const qr = useMemo(() => (uri ? encodeQr(uri) : null), [uri]);
  if (!qr) return null;
  const dim = qr.size + 8;
  let d = '';
  for (let y = 0; y < qr.size; y++) for (let x = 0; x < qr.size; x++) if (qr.modules[y][x]) d += `M${x + 4} ${y + 4}h1v1h-1z`;
  return (
    <svg data-testid="twofa-qr" viewBox={`0 0 ${dim} ${dim}`} width="120" height="120" shapeRendering="crispEdges" role="img" aria-label="QR code for your authenticator app">
      <rect width={dim} height={dim} fill="#FFFFFF" />
      <path d={d} fill="#22272E" />
    </svg>
  );
}

/** The server's privileged roles (twofa.ts PRIVILEGED_2FA_ROLES): required once the flag is on. */
const PRIVILEGED = new Set(['OWNER', 'SUPER_ADMIN', 'OPERATOR', 'ADMIN', 'PARTNER', 'CAREGIVER', 'STAFF']);
const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const SOFT = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);';
const DANGER = 'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.1);border:1px solid rgba(179,38,30,.34);';
const PRIMARY = 'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;';
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const CODE_IN = 'display:block;width:100%;box-sizing:border-box;height:34px;margin-top:6px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:14px;letter-spacing:.14em;font-variant-numeric:tabular-nums;';

/** A code (or password) field for a confirm dialog that needs a fresh re-auth. */
function ReauthField({ onChange, label }: { onChange: (v: string) => void; label: string }) {
  return (
    <label style={sx('display:block;font-size:16px;color:#3C454F;margin-top:6px;')}>
      {label}
      <input type="text" inputMode="numeric" autoComplete="one-time-code" onChange={(e) => onChange(e.target.value.trim())} style={sx(CODE_IN)} />
    </label>
  );
}

/** Two-factor (design seIsTwoFactor, T9585; tf* L22626-22816, seGuard L21695-21697). */
export function TwoFactorSection() {
  const { client, session } = useSession();
  const { role } = useConsoleRole();
  const { lens, ask, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const status = useApi((c, s) => twofaStatus(c, { signal: s }), []);
  const passkeys = useApi((c, s) => passkeyList(c, { signal: s }), []);
  const devices = useApi((c, s) => trustedDevices(c, { signal: s }), []);

  const [step, setStep] = useState<'start' | 'confirm' | 'codes' | 'done' | null>(null);
  const [enroll, setEnroll] = useState<{ secret: string; otpauth: string; codes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const [screen, setScreen] = useState<'Challenge' | 'Signup prompt'>('Challenge');
  const [chBackup, setChBackup] = useState(false);
  const [chCode, setChCode] = useState('');
  // Revoking a trusted device asks first (nothing is sent on the first click).
  const [revoking, setRevoking] = useState<{ id: string; label: string } | null>(null);
  const reauthRef = useRef('');

  const required = status.data ? status.data.required || PRIVILEGED.has(String(role)) : PRIVILEGED.has(String(role));
  const enrolled = status.data?.enrolled === true;
  const cur = step ?? (enrolled ? 'done' : 'start');
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const begin = () => run(async () => {
    const r = await twofaSessionEnroll(client, { body: {} });
    setEnroll({ secret: r.secret, otpauth: r.otpauth, codes: r.backupCodes });
    setCode('');
    setStep('confirm');
  });
  const confirm = () => run(async () => {
    if (code.length !== 6) return;
    await twofaSessionVerify(client, { body: { code } });
    setStep('codes');
  });
  const addPasskey = () => run(async () => {
    const o = (await passkeyRegisterOptions(client, { body: {} })) as Record<string, any>;
    const publicKey: PublicKeyCredentialCreationOptions = {
      ...o,
      challenge: base64urlToBuffer(o.challenge),
      user: { ...o.user, id: base64urlToBuffer(o.user.id) },
      excludeCredentials: (o.excludeCredentials ?? []).map((c: { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }) => ({ ...c, id: base64urlToBuffer(c.id) })),
    } as PublicKeyCredentialCreationOptions;
    const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
    if (!cred) throw Object.assign(new Error('No passkey was created.'), { message: 'No passkey was created.' });
    const r = cred.response as AuthenticatorAttestationResponse;
    await passkeyRegisterVerify(client, {
      body: {
        response: {
          id: cred.id, rawId: bufferToBase64url(cred.rawId), type: cred.type, clientExtensionResults: cred.getClientExtensionResults(),
          response: { clientDataJSON: bufferToBase64url(r.clientDataJSON), attestationObject: bufferToBase64url(r.attestationObject), transports: r.getTransports?.() ?? [] },
        },
        deviceName: navigator.platform || 'This device',
      },
    });
    passkeys.reload();
  });

  const pks = passkeys.data?.passkeys ?? [];
  const devs = (devices.data?.devices ?? []).filter((d) => !d.revoked_at);
  const flagText = status.data ? (status.data.enforced ? 'ENFORCED' : 'NOT YET ENFORCED') : notServed(status.error) ? 'STATUS NOT SERVED YET' : '–';
  const roleName = session?.user.name ?? '';

  return (
    <div data-screen-label="Two-factor" data-testid="settings-two-factor" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx(CAPT)}>Security &middot; two-factor</span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
        <span style={sx(chipT(status.data?.enforced ? '#1F6B52' : '#5B6B7C', status.data?.enforced ? 'rgba(47,138,111,.16)' : 'rgba(91,107,124,.18)'))}>{I(flagText)}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.55;max-width:92ch;text-wrap:pretty;')}>
        {I(status.data?.enforced
          ? 'Required now. Operator, partner, facility admin and care staff can see cross-resident PHI, so they must enroll. Residents, family and members are exempt: they are consumers, not custodians.'
          : 'Available now, and not yet required, so nobody is locked out before they have enrolled. Operator, partner, facility admin and care staff can see cross-resident PHI, so they must enroll. Residents, family and members are exempt: they are consumers, not custodians.')}
      </div>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:10px;')}>
        <span style={sx(chipT(required ? '#B3261E' : '#1F6B52', required ? 'rgba(179,38,30,.14)' : 'rgba(47,138,111,.16)'))}>{I(required ? 'REQUIRED FOR YOUR ROLE' : 'EXEMPT')}</span>
        <span style={sx('font-size:16px;color:#3C454F;text-wrap:pretty;')}>
          {I(required
            ? 'Your role holds cross-resident PHI, so a second factor will be required once your organization turns it on.'
            : ['SALES', 'DEVELOPER'].includes(String(role)) ? 'Your role is exempt: it does not carry cross-resident PHI.' : 'Your role is exempt: a consumer account never carries cross-resident PHI.')}
        </span>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,400px),1fr));gap:0 30px;align-items:start;margin-top:4px;')}>
        <div style={sx('min-width:0;')}>
          {lens.lens === 'Care Staff' && (
            <div style={sx('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;row-gap:7px;background:rgba(79,138,214,.09);border:1px solid rgba(47,92,151,.28);border-radius:11px;padding:12px 14px;margin-top:14px;')}>
              <span style={sx('display:inline-flex;align-items:center;height:20px;padding:0 9px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;color:#fff;background:#2F5C97;')}>PASSKEY FIRST</span>
              <span style={sx('flex:1 1 200px;min-width:min(100%,200px);font-size:16px;color:#3C454F;line-height:1.55;text-wrap:pretty;')}>
                {I('Your role signs in on shared floor devices, so a passkey comes first here: face, fingerprint or the tablet’s own unlock, under two seconds. A six-digit code is the fallback, not the default. If signing in on a floor tablet is slow, say so rather than working around it.')}
              </span>
            </div>
          )}
          <div style={sx(CAPT + 'margin:16px 0 9px;')}>Authenticator app &middot; TOTP</div>
          {cur === 'start' && (
            <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
              <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>Six digits from an app on your phone, rotating every 30 seconds.</div>
              <div style={sx('margin-top:11px;')}>
                <a href="#" {...wr(() => void begin())} style={sx(PRIMARY)}>Set up authenticator</a>
              </div>
            </div>
          )}
          {cur === 'confirm' && enroll && (
            <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
              <div style={sx('display:flex;align-items:flex-start;gap:15px;flex-wrap:wrap;row-gap:13px;')}>
                <a href={enroll.otpauth} aria-label="Scan with your authenticator app, or tap to open it" style={sx('flex:0 0 auto;min-width:0;width:132px;height:132px;border-radius:9px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);display:flex;align-items:center;justify-content:center;text-decoration:none;')}>
                  <OtpQr uri={enroll.otpauth} />
                </a>
                <div style={sx('flex:1 1 200px;min-width:min(100%,200px);')}>
                  <div style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;')}>Setup key</div>
                  <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;margin-top:4px;')}>{I(enroll.secret.replace(/(.{4})/g, '$1 ').trim())}</div>
                  <div style={sx('font-size:16px;color:#54606C;margin-top:8px;line-height:1.5;text-wrap:pretty;')}>
                    {I('Add it to Google Authenticator or Authy: scan the code, tap it on your phone, or type the key. Not SMS - SMS needs Twilio and app-TOTP is the stronger factor anyway.')}
                  </div>
                  <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:7px;margin-top:12px;')}>
                    <input type="text" aria-label="Six-digit code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder="000000" style={sx('box-sizing:border-box;width:118px;height:26px;padding:0 12px;border-top:1px solid rgba(34,39,46,.14);font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.14em;color:#22272E;')} />
                    <a href="#" onClick={(e) => { pre(e); void confirm(); }} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (code.length === 6 ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.06);color:#54606C;border:1px solid rgba(34,39,46,.12);pointer-events:none;'))}>
                      {I(code.length === 6 ? 'Confirm code' : 'Enter 6 digits')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
          {cur === 'codes' && enroll && (
            <div style={sx('box-sizing:border-box;background:rgba(179,38,30,.05);border:1px solid rgba(179,38,30,.28);border-radius:10px;padding:9px 2px;')}>
              <div style={sx('font-size:16px;font-weight:700;color:#B3261E;')}>{enroll.codes.length === 10 ? 'Ten backup codes' : enroll.codes.length + ' backup codes'}</div>
              <div style={sx('font-size:16px;color:#54606C;margin-top:4px;line-height:1.5;max-width:80ch;text-wrap:pretty;')}>
                {I('These codes are shown once and never again. Each works a single time. Store them somewhere that is not the device holding your authenticator.')}
              </div>
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(112px,1fr));gap:7px;margin-top:11px;')}>
                {enroll.codes.map((c) => (
                  <div key={c} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:7px 9px;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;text-align:center;')}>{I(c)}</div>
                ))}
              </div>
              <div style={sx('margin-top:12px;')}>
                <a href="#" onClick={(e) => { pre(e); setStep('done'); status.reload(); }} style={sx(PRIMARY)}>I have saved these</a>
              </div>
            </div>
          )}
          {cur === 'done' && (
            <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
              <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;')}>
                <span style={sx(chipT('#1F6B52', 'rgba(47,138,111,.16)'))}>TOTP ENROLLED</span>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;color:#54606C;')}>Six-digit codes, 30-second window.</span>
                <a
                  href="#"
                  {...wr(() => ask({
                    sec: 'Two-factor', title: 'Regenerate backup codes?', body: 'Your old backup codes stop working as soon as new ones are made.', btn: 'Regenerate',
                    extra: <ReauthField label="A code from your authenticator" onChange={(v) => { reauthRef.current = v; }} />,
                    go: async () => {
                      const r = await twofaBackupRegenerate(client, { body: { code: reauthRef.current, confirm: true } });
                      setEnroll({ secret: '', otpauth: '', codes: r.backupCodes });
                      setStep('codes');
                    },
                  }))}
                  style={sx(SOFT)}
                >
                  Regenerate backup codes
                </a>
                <a
                  href="#"
                  {...wr(() => ask({
                    sec: 'Two-factor', title: 'Turn off two-factor?', body: 'Your account falls back to password only. Anyone with your password can sign in.', btn: 'Turn off',
                    extra: <ReauthField label="A code from your authenticator" onChange={(v) => { reauthRef.current = v; }} />,
                    go: async () => {
                      await twofaDisable(client, { body: { code: reauthRef.current } });
                      setStep('start');
                      status.reload();
                    },
                  }))}
                  style={sx(DANGER)}
                >
                  Disable
                </a>
              </div>
            </div>
          )}
          <ErrLine error={err} />
          <div style={sx(CAPT + 'margin:16px 0 9px;')}>Passkeys &middot; WebAuthn</div>
          <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
            <div style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:88ch;text-wrap:pretty;')}>
              {I('A passkey satisfies the same gate as TOTP, and login offers passkey first with TOTP as fallback. Face ID, Windows Hello and hardware keys all count.')}
            </div>
            {pks.map((p) => (
              <div key={p.credential_id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')}>
                  <span style={sx('display:block;font-size:16px;font-weight:600;')}>{I(p.device_name ?? 'Passkey')}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>
                    {I('added ' + day(p.created_at))}
                    {' · '}
                    {I(p.last_used_at ? 'last used ' + day(p.last_used_at) : 'never used')}
                  </span>
                </span>
                <a
                  href="#"
                  {...wr(() => ask({
                    sec: 'Two-factor', title: 'Delete passkey ' + (p.device_name ?? '') + '?', body: 'That device can no longer sign you in.', btn: 'Delete passkey',
                    extra: <ReauthField label="A code from your authenticator" onChange={(v) => { reauthRef.current = v; }} />,
                    go: async () => {
                      await passkeyDelete(client, { body: { credentialId: p.credential_id, code: reauthRef.current, confirm: true } });
                      passkeys.reload();
                    },
                  }))}
                  style={sx('font-size:16px;color:#B3261E;text-decoration:none;white-space:nowrap;')}
                >
                  delete
                </a>
              </div>
            ))}
            {passkeys.data && pks.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:9px 0 2px;')}>No passkey registered. TOTP still satisfies the gate.</div>}
            {passkeys.error && <ErrLine error={passkeys.error} />}
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:7px;margin-top:10px;padding-top:10px;border-top:1px solid rgba(34,39,46,.07);')}>
              <a href="#" {...wr(() => void addPasskey())} style={sx(SOFT)}>+ Add a passkey</a>
              <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;color:#B3261E;line-height:1.45;text-wrap:pretty;')}>{I('Passkeys are in final testing. Ask before you rely on one as your only way in.')}</span>
            </div>
          </div>
          <div style={sx(CAPT + 'margin:16px 0 9px;')}>Trusted devices</div>
          <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
            {devs.map((d) => (
              <div key={d.id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')}>
                  <span style={sx('display:block;font-size:16px;font-weight:600;')}>{I(d.label ?? 'A trusted browser')}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>
                    {I('trusted until ' + day(d.expires_at))}
                    {' · '}
                    {I(d.last_used_at ? 'last used ' + day(d.last_used_at) : 'not used since')}
                  </span>
                </span>
                <a href="#" {...wr(() => setRevoking({ id: d.id, label: d.label ?? 'this trusted browser' }))} style={sx('font-size:16px;color:#B3261E;text-decoration:none;white-space:nowrap;')}>revoke</a>
              </div>
            ))}
            {devices.data && devs.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:8px 0 2px;')}>No trusted device. Every sign-in asks for the second factor.</div>}
            {revoking && canWrite && (
              <ConfirmDialog
                title={'Revoke ' + revoking.label + '?'}
                body="It asks for your second factor at its next sign-in. To trust it again, tick Trust this device for 30 days when you sign in there."
                confirmLabel="Revoke"
                danger
                onConfirm={async () => {
                  await trustedDeviceRevoke(client, { body: { id: revoking.id } });
                  devices.reload();
                  changed();
                }}
                onClose={() => setRevoking(null)}
              />
            )}
            {devices.error && <ErrLine error={devices.error} />}
            <div style={sx('font-size:16px;color:#54606C;margin-top:9px;line-height:1.5;text-wrap:pretty;')}>
              {I('Trusting a device skips the second factor for 30 days - never the password. Revoking takes effect on the next sign-in.')}
            </div>
          </div>
          <div style={sx(CAPT + 'margin:16px 0 9px;')}>What sign-in looks like</div>
          <div style={sx('box-sizing:border-box;padding:9px 2px;')}>
            {[['Password accepted', 'the server returns a short-lived pending token, not a session'], ['Second factor', 'six digits, or "use a backup code"'], ['Session minted', 'only after the challenge passes'], ['Lockout', 'the factor locks after five consecutive failures']].map((c) => (
              <div key={c[0]} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;padding:7px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('font-size:16px;font-weight:600;width:132px;flex:0 1 auto;min-width:0;')}>{I(c[0])}</span>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{I(c[1])}</span>
              </div>
            ))}
            <div style={sx('font-size:16px;color:#54606C;margin-top:9px;line-height:1.5;text-wrap:pretty;')}>
              {I('Secret sealed AES-256-GCM at rest, backup codes stored hashed, and neither the raw secret nor a raw code is ever persisted.')}
            </div>
          </div>
        </div>
        <div style={sx('min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin:0 0 9px;')}>
            <span style={sx(CAPT)}>The two auth screens</span>
            <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
            {(['Challenge', 'Signup prompt'] as const).map((n) => (
              <a key={n} href="#" aria-pressed={screen === n} onClick={(e) => { pre(e); setScreen(n); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:16px;text-decoration:none;white-space:nowrap;' + (screen === n ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'))}>
                {I(n)}
              </a>
            ))}
          </div>
          <div data-testid="twofa-preview" style={sx('box-sizing:border-box;background:rgba(34,39,46,.05);border:1px solid rgba(34,39,46,.1);border-radius:12px;padding:22px;display:flex;justify-content:center;')}>
            {screen === 'Challenge' && (
              <div style={sx('box-sizing:border-box;width:100%;max-width:380px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;padding:26px 24px;box-shadow:0 8px 28px rgba(34,39,46,.09);')}>
                <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:18px;')}>
                  <img src="/aithora-mark.svg" alt="Aithora" style={sx('width:24px;height:24px;flex:0 0 24px;object-fit:contain;')} />
                  <span style={sx('font-size:14px;font-weight:700;letter-spacing:-.01em;')}>Aithora</span>
                </div>
                <div>
                  <div style={sx('font-size:17px;font-weight:700;letter-spacing:-.015em;')}>{I(chBackup ? 'Enter a backup code' : 'Enter your code')}</div>
                  <div style={sx('font-size:16px;color:#54606C;margin-top:7px;line-height:1.5;text-wrap:pretty;')}>
                    {I(chBackup ? 'One of the ten you saved at enrollment. Each works once, then it is spent.' : 'Six digits from your authenticator. Your password was accepted - this is the second factor, not a retry.')}
                  </div>
                  <a href="#" onClick={pre} style={sx('display:flex;align-items:center;justify-content:center;gap:8px;height:40px;border-radius:9px;text-decoration:none;background:#FBF8F1;border:1px solid rgba(34,39,46,.18);color:#22272E;font-size:16px;font-weight:600;margin-top:16px;')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={sx('flex:0 0 15px;')} aria-hidden="true">
                      <rect x="4" y="10.5" width="16" height="10" rx="2.4" />
                      <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" />
                    </svg>
                    {I('Sign in with a passkey')}
                  </a>
                  <div style={sx('font-size:16px;color:#54606C;margin-top:7px;line-height:1.45;text-wrap:pretty;')}>{I('Login offers passkey first and falls back to a code, because Face ID is one gesture and six digits is four.')}</div>
                  <div style={sx('display:flex;align-items:center;gap:10px;margin:16px 0;')}>
                    <span style={sx('flex:1;height:1px;background:rgba(34,39,46,.12);')} />
                    <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;')}>or</span>
                    <span style={sx('flex:1;height:1px;background:rgba(34,39,46,.12);')} />
                  </div>
                  <input
                    type="text"
                    aria-label="Preview code"
                    value={chCode}
                    onChange={(e) => setChCode(chBackup ? e.target.value.toUpperCase().slice(0, 9) : e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    placeholder={chBackup ? 'XXXX-XXXX' : '000000'}
                    style={sx('box-sizing:border-box;width:100%;height:46px;padding:0 15px;border:1px solid rgba(34,39,46,.2);border-radius:9px;background:#FBF8F1;font-variant-numeric:tabular-nums;font-size:19px;letter-spacing:.2em;text-align:center;color:#22272E;')}
                  />
                  <div style={sx('margin-top:13px;')}>
                    <a href="#" aria-disabled="true" title="A preview of the sign-in screen: nothing is checked here." onClick={pre} style={sx('display:inline-flex;align-items:center;justify-content:center;height:40px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;width:100%;box-sizing:border-box;')}>
                      Verify
                    </a>
                  </div>
                  <div style={sx('text-align:center;margin-top:13px;')}>
                    <a href="#" onClick={(e) => { pre(e); setChBackup(!chBackup); setChCode(''); }} style={sx('font-size:16px;color:#2F5C97;text-decoration:none;')}>{I(chBackup ? 'Use a 6-digit code instead' : 'Use a backup code instead')}</a>
                  </div>
                </div>
              </div>
            )}
            {screen === 'Signup prompt' && (
              <div style={sx('box-sizing:border-box;width:100%;max-width:420px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:10px;padding:26px 24px;box-shadow:0 8px 28px rgba(34,39,46,.09);')}>
                <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:16px;')}>
                  <img src="/aithora-mark.svg" alt="Aithora" style={sx('width:24px;height:24px;flex:0 0 24px;object-fit:contain;')} />
                  <span style={sx('font-size:14px;font-weight:700;letter-spacing:-.01em;')}>Aithora</span>
                  <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
                  <span style={sx(chipT(required ? '#B3261E' : '#1F6B52', required ? 'rgba(179,38,30,.14)' : 'rgba(47,138,111,.16)'))}>{I(required ? 'REQUIRED FOR YOUR ROLE' : 'EXEMPT')}</span>
                </div>
                <div style={sx('font-size:18px;font-weight:700;letter-spacing:-.015em;')}>Set up your second factor</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:5px;')}>{I('Signing in as ' + (role ? ROLE_LABEL[role] : '') + (roleName ? ' · ' + roleName : ''))}</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:9px;line-height:1.55;text-wrap:pretty;')}>
                  {I('Your role reads cross-resident PHI, so Aithora asks for a second factor before you go further. It takes about a minute, and you are never hard-locked out - you can come back to this from Settings.')}
                </div>
                <div style={sx('display:flex;flex-direction:column;gap:9px;margin-top:16px;')}>
                  {[['Passkey', 'Face ID, Windows Hello or a hardware key. One gesture, nothing to type.', true], ['Authenticator app', 'Six digits from Google Authenticator or Authy, rotating every 30 seconds.', false]].map((o) => (
                    <a key={String(o[0])} href="#" onClick={pre} className="se-hover-border" style={sx('box-sizing:border-box;display:block;padding:12px 14px;border-radius:10px;text-decoration:none;color:#22272E;' + (o[2] ? 'background:rgba(79,138,214,.1);border:1px solid rgba(47,92,151,.34);' : 'background:#FBF8F1;border:1px solid rgba(34,39,46,.13);'))}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;')}>{I(o[0])}</span>
                      <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:3px;line-height:1.45;text-wrap:pretty;')}>{I(o[1])}</span>
                    </a>
                  ))}
                </div>
                <div style={sx('text-align:center;margin-top:15px;')}>
                  <a href="#" onClick={pre} style={sx('font-size:16px;color:#2F5C97;text-decoration:none;')}>Remind me at next sign-in</a>
                </div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:10px;line-height:1.5;text-wrap:pretty;')}>
                  {I('Deferring is allowed until the flag is on. After that, an unenrolled required user is routed here rather than refused - the difference between a gate and a wall.')}
                </div>
              </div>
            )}
          </div>
          {!canWrite && <span className="sr-only">{roReason}</span>}
        </div>
      </div>
    </div>
  );
}
